/**
 * Video Thumbnail Service - Sprint 20+
 * Extracts and caches video frame thumbnails for scene previews and transition animations
 */

import { SceneThumbnailOptions } from '../../../types/scene';
import { logger } from '../utils/logger';

export interface ThumbnailExtractionResult {
  dataUrl: string;
  blob: Blob;
  width: number;
  height: number;
}

export class VideoThumbnailService {
  private static readonly DEFAULT_OPTIONS: SceneThumbnailOptions = {
    width: 320,
    height: 180,
    quality: 0.85,
    format: 'jpeg',
  };

  static async extractFrameFromVideo(
    videoBlob: Blob,
    framePosition: 'first' | 'middle' | 'last' | number = 'middle',
    options: Partial<SceneThumbnailOptions> = {}
  ): Promise<ThumbnailExtractionResult> {
    const opts = { ...this.DEFAULT_OPTIONS, ...options };

    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      const objectUrl = URL.createObjectURL(videoBlob);

      video.onloadedmetadata = () => {
        const duration = video.duration;
        
        let seekTime = 0;
        if (framePosition === 'first') {
          seekTime = 0.1;
        } else if (framePosition === 'middle') {
          // Capture at 50% (true middle) to avoid black frames at start
          seekTime = duration * 0.5;
        } else if (framePosition === 'last') {
          seekTime = Math.max(0, duration - 0.5);
        } else {
          seekTime = Math.min(framePosition, duration);
        }

        console.log(`📸 [VideoThumbnailService] Seeking to ${framePosition} frame:`, {
          position: framePosition,
          videoDuration: duration,
          seekTime: seekTime,
          percentage: Math.round((seekTime / duration) * 100) + '%'
        });

        video.currentTime = seekTime;
      };

      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            throw new Error('Failed to get canvas context');
          }

          const videoAspectRatio = video.videoWidth / video.videoHeight;
          const targetAspectRatio = opts.width / opts.height;

          let drawWidth = opts.width;
          let drawHeight = opts.height;

          if (videoAspectRatio > targetAspectRatio) {
            drawHeight = opts.width / videoAspectRatio;
          } else {
            drawWidth = opts.height * videoAspectRatio;
          }

          canvas.width = drawWidth;
          canvas.height = drawHeight;

          ctx.drawImage(video, 0, 0, drawWidth, drawHeight);

          const mimeType = `image/${opts.format}`;
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to create thumbnail blob'));
                return;
              }

              const dataUrl = canvas.toDataURL(mimeType, opts.quality);
              
              URL.revokeObjectURL(objectUrl);
              video.remove();

              resolve({
                dataUrl,
                blob,
                width: drawWidth,
                height: drawHeight,
              });

              logger.info('[VideoThumbnailService] Thumbnail extracted successfully', {
                position: framePosition,
                size: `${drawWidth}x${drawHeight}`,
                blobSize: blob.size,
              });
            },
            mimeType,
            opts.quality
          );
        } catch (error) {
          URL.revokeObjectURL(objectUrl);
          video.remove();
          reject(error);
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        video.remove();
        reject(new Error('Failed to load video for thumbnail extraction'));
      };

      video.src = objectUrl;
    });
  }

  static async extractBothFrames(
    videoBlob: Blob,
    options: Partial<SceneThumbnailOptions> = {}
  ): Promise<{
    firstFrame: ThumbnailExtractionResult;
    lastFrame: ThumbnailExtractionResult;
  }> {
    try {
      const [firstFrame, lastFrame] = await Promise.all([
        this.extractFrameFromVideo(videoBlob, 'first', options),
        this.extractFrameFromVideo(videoBlob, 'last', options),
      ]);

      return { firstFrame, lastFrame };
    } catch (error) {
      logger.error('[VideoThumbnailService] Failed to extract both frames:', error);
      throw error;
    }
  }

  static async createThumbnailFromDataUrl(
    dataUrl: string,
    targetWidth: number = 160,
    targetHeight: number = 90
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        canvas.width = targetWidth;
        canvas.height = targetHeight;
        
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        
        const resizedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        resolve(resizedDataUrl);
      };

      img.onerror = () => {
        reject(new Error('Failed to load image for resizing'));
      };

      img.src = dataUrl;
    });
  }

  static blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert blob to data URL'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
